import { useEffect, useState } from 'react';
import { err, ok, Result } from 'neverthrow';
import { t } from 'i18next';
import { useStore } from '@/store';
import { useNotify } from './use-notify';
import {
  useDownloadRecentFile,
  useRemoveRecentFile,
  useTemplate,
  useUploadRecentFile,
} from '../api/templates/query';
import { safeAsync } from '@morph-mapper/utils';
import { RecentFile } from '@/types';
import { decodeRecentFilePayload } from './recent-file-payload';
import { downloadFile as saveFileToDisk } from '@/utils';

enum RecentFilesErrorType {
  FileNotFound = 'FILE_NOT_FOUND',
  DownloadFailed = 'DOWNLOAD_FAILED',
  ParseFailed = 'PARSE_FAILED',
}

type RecentFilesError = {
  type: RecentFilesErrorType;
  message: string;
};

const sortRecentFiles = (files: RecentFile[]) =>
  [...files].sort(
    (a, b) => new Date(b.usedAt).getTime() - new Date(a.usedAt).getTime(),
  );

export const useRecentFiles = (templateId: string) => {
  /* Global state */
  const userId = useStore(({ config: c }) => c.getUserId());
  /* Local state */
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  /* Hooks */
  const { error: notifyError } = useNotify();
  /* Queries */
  const { data: template, error: templateError } = useTemplate(templateId);
  const { mutateAsync: removeRecentFileAsync } = useRemoveRecentFile();
  const { mutateAsync: uploadRecentFileAsync } = useUploadRecentFile();
  const { mutateAsync: downloadRecentFileAsync } = useDownloadRecentFile();

  useEffect(() => {
    if (!template) {
      return;
    }

    setRecentFiles(sortRecentFiles(template.recentFiles || []));
  }, [template?.recentFiles]);

  useEffect(() => {
    if (!templateError) {
      return;
    }

    notifyError(t('message.error.recentFilesFetchFailed'));
  }, [templateError, notifyError]);

  const addRecentFile = async (file: File) => {
    const uploadResult = await safeAsync(() =>
      uploadRecentFileAsync({
        templateId,
        file,
        userId,
      }),
    );
    if (uploadResult.isErr()) {
      return;
    }

    setRecentFiles(sortRecentFiles(uploadResult.value.recentFiles || []));
  };

  const retrieveFileBytes = async (
    fileId: string,
  ): Promise<Result<ArrayBuffer, RecentFilesError>> => {
    const downloadResult = await safeAsync(() =>
      downloadRecentFileAsync(fileId),
    );
    if (downloadResult.isErr()) {
      return err({
        type: RecentFilesErrorType.DownloadFailed,
        message: downloadResult.error.message,
      });
    }

    const parseResult = await safeAsync(async () =>
      decodeRecentFilePayload(downloadResult.value),
    );

    if (parseResult.isErr()) {
      return err({
        type: RecentFilesErrorType.ParseFailed,
        message: parseResult.error.message,
      });
    }

    return ok(parseResult.value);
  };

  const resolveRecentFileBytes = async (
    fileId: string,
  ): Promise<
    Result<{ recentFile: RecentFile; bytes: ArrayBuffer }, RecentFilesError>
  > => {
    const recentFile = recentFiles.find((file) => file.fileId === fileId);
    if (!recentFile) {
      return err({
        type: RecentFilesErrorType.FileNotFound,
        message: 'Recent file not found',
      });
    }

    const bytesResult = await retrieveFileBytes(fileId);
    if (bytesResult.isErr()) {
      return err(bytesResult.error);
    }

    return ok({ recentFile, bytes: bytesResult.value });
  };

  const loadRecentFile = async (
    fileId: string,
  ): Promise<Result<File, RecentFilesError>> => {
    const resolved = await resolveRecentFileBytes(fileId);
    if (resolved.isErr()) {
      return err(resolved.error);
    }

    const { recentFile, bytes } = resolved.value;
    const file = new File([bytes], recentFile.fileName, {
      type: recentFile.mimeType || 'application/octet-stream',
      lastModified: new Date(recentFile.usedAt).getTime(),
    });

    return ok(file);
  };

  const downloadRecentFile = async (
    fileId: string,
  ): Promise<Result<void, RecentFilesError>> => {
    const resolved = await resolveRecentFileBytes(fileId);
    if (resolved.isErr()) {
      // A missing entry is a caller/state bug; only surface retrieval failures.
      if (resolved.error.type !== RecentFilesErrorType.FileNotFound) {
        notifyError(t('message.error.recentFilesDownloadFailed'));
      }
      return err(resolved.error);
    }

    const { recentFile, bytes } = resolved.value;
    const blob = new Blob([bytes], {
      type: recentFile.mimeType || 'application/octet-stream',
    });
    saveFileToDisk(blob, recentFile.fileName);

    return ok(undefined);
  };

  const removeRecentFile = async (fileId: string) => {
    const result = await safeAsync(() =>
      removeRecentFileAsync({
        templateId,
        fileId,
      }),
    );
    if (result.isErr()) {
      return;
    }

    setRecentFiles(sortRecentFiles(result.value.recentFiles || []));
  };

  return {
    recentFiles,
    addRecentFile,
    loadRecentFile,
    downloadRecentFile,
    removeRecentFile,
  };
};
